import {
  VentureTagCreateDocument,
  VentureTagDeleteDocument,
  VentureTagGetByIdDocument,
  VentureTagListDocument,
  VentureTagUpdateDocument,
  VentureTagsPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import {
  IGetTagArgs,
  ITagListReturnType,
  ITagModel,
} from "api/services/library/tags";
import { ResourceEnum, ScopeEnum } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getVentureTagsList(
  variables: IGetTagArgs
): Promise<ITagListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureTagsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureTagsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureTag(
  name: string,
  description: string
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureTagCreateDocument,
      variables: {
        name,
        description,
      },
      fetchPolicy: "no-cache",
    });

    // Evict all tag queries so they refetch with the new item
    executeEviction(client, {
      typename: "VentureTag",
      fieldNames: ["ventureTagsPaginated", "ventureTagList"],
    });

    return res?.data?.ventureTagCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureTag(
  tagId: number,
  name: string,
  description: string
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureTagUpdateDocument,
      variables: {
        tagId,
        name,
        description,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "VentureTag",
      id: tagId.toString(),
      fieldNames: [
        "ventureTagsPaginated",
        "ventureTagList",
        "ventureTagGetById",
      ],
    });

    return res?.data?.ventureTagUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getVentureTag(tagId: number): Promise<ITagModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureTagGetByIdDocument,
      variables: {
        tagId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureTagGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureTag(tagId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureTagDeleteDocument,
      variables: {
        tagId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "VentureTag",
      id: tagId.toString(),
      fieldNames: [
        "ventureTagsPaginated",
        "ventureTagList",
        "ventureTagGetById",
      ],
    });

    return Boolean(res?.data?.ventureTagDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureTags(
  scope: ScopeEnum,
  resource: ResourceEnum
): Promise<Pick<ITagModel, "tagId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureTagListDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureTagList ?? [];
  } catch (error) {
    throw error;
  }
}
